import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import FunnelQuestionScreen from "@common/components/funnel/FunnelQuestionScreen"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import { SCENE_BACKGROUND } from "@common/components/funnel/FunnelScene"
import bubbleIllustration from "@assets/stage/funnel/bubble.webp"
import { apiRequest } from "@data/redux/actions/api"
import { elide } from "@common/utils/activationSuiteDisplay"
import { useStageFunnel } from "./context/StageFunnelContext"
import { useStageTrack } from "./hooks/useStageTrack"
import { useStageFunnelGuard } from "./hooks/useStageFunnelGuard"
import { stageFunnelService } from "./services/stageFunnelService"
import { STAGE_BESOINS, toggleBesoinInList } from "./constants/stageContent"

/**
 * Étape diagnostic du funnel Stage — un seul écran, « une question par écran »
 * (template Preply via <FunnelQuestionScreen />) : les besoins spécifiques de
 * l'élève (multi, chips dys/TDAH/HPI/autre/aucune — « Aucune » exclusif ; pas de
 * champ texte libre dans ce MVP).
 *
 * Les deux autres questions qui vivaient ici sont parties au compositeur :
 * l'objectif macro (04/09) puis le niveau global (05/09). Le curseur par
 * matière dit où en est l'élève et où il va, matière par matière — ce qu'un
 * « moyen » global ne disait pas. L'`objective` que le backend attend est
 * dérivé de ces curseurs (voir StageConfiguratorScreen).
 *
 * Rien ici n'impacte le prix : le diagnostic personnalise le CONTENU (programme
 * + brief tuteur via les besoins durables). La complétion persiste les réponses
 * (updateStageAnswers → auto-transition `stage_diag_completed` côté backend)
 * puis navigue vers le COMPOSITEUR.
 */

// Étape globale affichée dans la barre de progression. Séquence à plat :
// classe = 1, spécificités = 2, compositeur = 3, programme = 4, tuteurs = 5,
// réservation = 6.
const FUNNEL_TOTAL_STEPS = 6
const DIAGNOSTIC_GLOBAL_STEP = 2

const StageDiagnosticScreen = (props) => {
    const { navigation, reduxFunnelStateId } = props
    const { state, update } = useStageFunnel()
    const track = useStageTrack()
    // Refresh-safe / URL-partageable (voir useStageFunnelGuard).
    const { userParam, nextParams } = useStageFunnelGuard()

    const [besoinsSpeciaux, setBesoinsSpeciaux] = useState(
        state.besoinsSpeciaux || []
    )

    // Pas de classe → on renvoie à l'étape 1. Quand un `user` est présent sur un
    // contexte froid, la garde gère déjà la reprise (stage/resume). Un abonné
    // reconnu n'a rien à répondre ici (retour du navigateur, URL rouverte).
    useEffect(() => {
        if (state.profileKnown) {
            navigation.replace("stage/configurator", nextParams)
        } else if (!state.studentClass && !userParam) {
            navigation.navigate("stage/start")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Persiste le diagnostic puis ouvre le compositeur.
    const submitDiagnostic = () => {
        // Valeur neutre : le vrai objectif se lit sur les curseurs par matière
        // du compositeur, qui l'écrasera avant la composition du programme.
        const objective = state.objective || "combler_lacunes"
        update({ objective, besoinsSpeciaux })
        const stateId = state.funnelStateId || reduxFunnelStateId
        if (stateId) {
            props.apiRequest(
                stageFunnelService.updateStageAnswers,
                { stateId },
                {
                    objective,
                    besoinsSpeciaux,
                    // Re-send the class so it's persisted even if the class step's
                    // own save was skipped (funnelStateId not yet synced there).
                    studentClass: state.studentClass,
                    studentClassRentree: state.studentClassRentree,
                }
            )
        }
        track("stage_funnel_diagnostic_completed", {
            funnel_state_id: stateId,
            objective,
            besoins_speciaux: besoinsSpeciaux,
        })
        navigation.navigate("stage/configurator", nextParams)
    }

    const goBack = () => navigation.goBack()

    const firstname = state.studentFirstname || "votre enfant"

    // Optionnel → le CTA reste actif même sans sélection.
    return (
        <FunnelQuestionScreen
            illustration={bubbleIllustration}
            illustrationBgColor={SCENE_BACKGROUND}
            // Tournure neutre : aucune présupposition de genre de l'élève (pas
            // d'inversion « a-t-il » ni d'accord genré). Copy stage uniquement —
            // le brevet garde son propre écran.
            question={`Est-ce ${elide(firstname, "que")} a des spécificités que nous devrions connaître ?`}
            helper="Dys, TDAH, HPI… Vos réponses restent privées et servent uniquement à adapter les cours. Optionnel."
            options={STAGE_BESOINS.map((b) => ({
                id: b.id,
                label: b.label,
            }))}
            selectedIds={besoinsSpeciaux}
            onToggle={(id) =>
                setBesoinsSpeciaux((prev) => toggleBesoinInList(prev, id))
            }
            chipGrid
            multi
            canContinue
            ctaLabel="Continuer"
            onContinue={submitDiagnostic}
            step={DIAGNOSTIC_GLOBAL_STEP}
            total={FUNNEL_TOTAL_STEPS}
            onBack={goBack}
            HeaderComponent={FunnelStepHeader}
        />
    )
}

const mapStateToProps = (state) => ({
    reduxFunnelStateId: state.data?.stageFunnel?.funnelStateId ?? null,
})

const mapDispatchToProps = { apiRequest }

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(StageDiagnosticScreen)
